import { cacheSubForSid, getCachedSubForSid } from './oidcManager';

/**
 * initEsignSSO — ensures the browser holds a fresh OIDC access + refresh
 * token pair for the UV session identified by `sid`, and returns the
 * resolved OIDC `sub` (`<firmId>:<subUserId>`). Null when the UV session is
 * invalid or expired.
 *
 * Single round trip: POST `/oidc/trusted-token` with the UV SessionID +
 * browser cookies + `client_id=esign`. OidcIssuer validates the cookie
 * binding via the UV `checkSession` lib (imported directly — no HTTP hop)
 * and mints JWT tokens audienced to `esign`.
 *
 * Fast path: if we already have fresh tokens for this sid in localStorage,
 * return the cached sub without a network call.
 *
 * Dedup: concurrent callers for the same sid share a single in-flight
 * promise.
 *
 * Mirror of `Components/TrustShield/initTrustShieldSSO.ts`.
 */
const inFlightRefresh = new Map<string, Promise<string | null>>();

const AT_SAFETY_MARGIN_MS = 10_000;

interface TokenResponse {
    access_token: string;
    id_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
    scope: string;
    sub: string;
}

function storageKeyForSub(sub: string): string {
    const authority = `${window.location.origin}/oidc`;
    return `es-${sub}:oidc.user:${authority}:esign`;
}

function readStoredUser(sub: string): { expires_at?: number; refresh_token?: string } | null {
    try {
        const raw = localStorage.getItem(storageKeyForSub(sub));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return { expires_at: parsed.expires_at, refresh_token: parsed.refresh_token };
    } catch {
        return null;
    }
}

/** Decode a JWT's payload (middle segment, base64url) without verifying. */
function decodeJwtPayload(jwt: string): Record<string, any> | null {
    try {
        const parts = jwt.split('.');
        if (parts.length !== 3) return null;
        const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(padded));
    } catch {
        return null;
    }
}

/**
 * Write a User object in the shape oidc-client-ts expects, so every reader
 * of the same partition sees a coherent record:
 *   - useEsignAuth (ReactShell React) via UserManager.getUser()
 *   - the esign SPA's api/oidcRequest.ts via direct localStorage scan
 */
function writeStoredUser(token: TokenResponse): void {
    const expires_at = Math.floor(Date.now() / 1000) + token.expires_in;
    const profile = decodeJwtPayload(token.id_token) ?? { sub: token.sub };
    const user = {
        access_token: token.access_token,
        id_token: token.id_token,
        refresh_token: token.refresh_token,
        token_type: token.token_type,
        scope: token.scope,
        expires_at,
        profile,
        session_state: null,
    };
    localStorage.setItem(storageKeyForSub(token.sub), JSON.stringify(user));
}

async function fetchTrustedToken(sid: string): Promise<TokenResponse | null> {
    try {
        const body = new URLSearchParams({
            session_id: sid,
            client_id: 'esign',
            scope: 'openid profile esign:read esign:write',
        });
        const resp = await fetch('/oidc/trusted-token', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        });
        if (!resp.ok) return null;
        const data = await resp.json();
        if (!data?.access_token || !data?.id_token || !data?.refresh_token || !data?.sub) return null;
        return data as TokenResponse;
    } catch {
        return null;
    }
}

/**
 * Subscribe to /trusted-token refresh events. Fires once per successful
 * slow-path `POST /oidc/trusted-token` (fast-path cached returns do NOT
 * fire). Used by components that embed esign in an iframe to force a
 * reload when ReactShell recovers from token staleness. Returns an
 * unsubscribe fn.
 */
const refreshListeners = new Set<() => void>();
export function onEsignRefresh(cb: () => void): () => void {
    refreshListeners.add(cb);
    return () => { refreshListeners.delete(cb); };
}

export function initEsignSSO(sid: string): Promise<string | null> {
    const existing = inFlightRefresh.get(sid);
    if (existing) return existing;

    const promise = (async (): Promise<string | null> => {
        const cachedSub = getCachedSubForSid(sid);
        if (cachedSub) {
            const stored = readStoredUser(cachedSub);
            if (
                stored?.expires_at
                && stored.expires_at * 1000 - Date.now() > AT_SAFETY_MARGIN_MS
            ) {
                return cachedSub;
            }
        }

        const token = await fetchTrustedToken(sid);
        if (!token) return null;

        writeStoredUser(token);
        cacheSubForSid(sid, token.sub);
        refreshListeners.forEach((cb) => {
            try { cb(); } catch { /* swallow */ }
        });
        return token.sub;
    })().finally(() => { inFlightRefresh.delete(sid); });

    inFlightRefresh.set(sid, promise);
    return promise;
}
